import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import Select from 'components/form/Inputs/Select';
import Success from 'components/form/Success';
import { PageBack, PageHeadContainer } from 'components/PageHead';
import DOCUMENTATION_ROLE, {
  documentationRoles,
  DocumentationRoleValues,
} from 'enums/DocumentationRole';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import DocumentationFile from 'types/DocumentationFile';
import manageTextError from 'utils/manageTextError';
import privateAPI from 'utils/privateAxiosInstance';

const HeadContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 1.5rem;
    margin-right: 1rem;
  }
`;

const Form = styled.form`
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;
  h1 {
    color: black;
    margin-bottom: 0.5rem;
  }
  button {
    margin-left: auto;
    margin-right: 0;
  }
`;

const TextContainer = styled.div`
  color: var(--gray-500);
  margin-top: 2rem;
  p {
    margin-bottom: 1rem;
  }
`;

const regex = new RegExp('^(([a-z]+)(-?))*([a-z]+)$');

type State = Required<Pick<DocumentationFile, 'title' | 'filename'>> & {
  role: DocumentationRoleValues | null;
  file: File | null;
};

const initialState: State = {
  role: null,
  title: '',
  filename: '',
  file: null,
};

type FieldErrors = {
  [key in 'role' | 'title' | 'filename' | 'file']: boolean;
};

const initialFieldErrors: FieldErrors = {
  role: false,
  title: false,
  filename: false,
  file: false,
};

const Create: FC = () => {
  const [state, setState] = useState<State>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] =
    useState<FieldErrors>(initialFieldErrors);

  useEffect(() => {
    setFieldErrors({
      ...initialFieldErrors,
      filename: !!state.filename && !regex.test(state.filename),
    });
  }, [state]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSuccess(false);
    setError('');
    return setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeSelect: React.ChangeEventHandler<HTMLSelectElement> = (
    e,
  ) => {
    setSuccess(false);
    setError('');
    return setState({
      ...state,
      [e.target.name]: e.target.value || null,
    });
  };

  const handleChangeFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSuccess(false);
    setError('');
    return setState({
      ...state,
      [e.target.name]: e.target.files?.length ? e.target.files[0] : null,
    });
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError('');
    setFieldErrors(initialFieldErrors);

    const formData = new FormData();
    formData.append('role', state.role || '');
    formData.append('title', state.title);
    formData.append('filename', state.filename);
    state.file && formData.append('file', state.file);

    privateAPI
      .post('/files/documentation-file', formData)
      .then(() => {
        setSuccess(true);
      })
      .catch((err) => {
        if (err?.response?.data?.fields && err.response.data.fields[0]) {
          setFieldErrors({
            ...fieldErrors,
            [err.response.data.fields[0]]: true,
          });
        }
        manageTextError(err, setError);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <>
      <PageHeadContainer>
        <HeadContainer>
          <PageBack href="/admin/files" />
          <h1>Ajouter un fichier</h1>
        </HeadContainer>
      </PageHeadContainer>
      <Form onSubmit={handleSubmit}>
        {error && <Error>{error}</Error>}
        {success && <Success>Le fichier a bien été ajouté.</Success>}
        <Select
          label="Type d'utilisateur"
          value={state.role || ''}
          onChange={handleChangeSelect}
          name="role"
          required
          error={fieldErrors.role}
        >
          <option value="">-- Sélectionnez une option --</option>
          {documentationRoles.map((documentationRole, idx) => (
            <option key={idx} value={documentationRole}>
              {DOCUMENTATION_ROLE[documentationRole]}
            </option>
          ))}
        </Select>
        <Input
          name="title"
          label="Titre"
          value={state.title}
          onChange={handleChange}
          required
          error={fieldErrors.title}
        />
        <Input
          name="filename"
          label="Nom du fichier"
          value={state.filename}
          onChange={handleChange}
          required
          error={fieldErrors.filename}
        />
        <Input
          type="file"
          name="file"
          label="Fichier"
          onChange={handleChangeFile}
          required
          error={fieldErrors.file}
          value={state.file?.name}
        />
        <Button color="primary" loading={loading}>
          Ajouter le fichier
        </Button>
        <TextContainer>
          <p>
            Le formulaire ci-dessus permet de créer des fichiers qui seront
            ensuite accessibles et téléchargeables par les différents
            utilisateurs.
          </p>
          <p>Seuls les fichiers au format PDF sont acceptés.</p>
          <p>
            Pour chaque fichier, vous devez préciser le type d'utilisateur pour
            lequel il sera visible. Si vous souhaitez qu'un document soit
            accessible par 2 types d'utilisateurs différents, il vous faudra
            alors créer 2 fichiers différents (1 par type d'utilisateur).
          </p>
          <p>
            Le "Nom du fichier" correspond au nom sous lequel le fichier sera
            enregistré sur le serveur ainsi que sur la machine de l'utilisateur.
            Il ne doit comporter que des lettres minuscules, et éventuellement
            des tirets (ex: fichier-mentore). Il est unique pour chaque fichier
            et est définitif.
          </p>
          <p>
            Le "Titre" correspond au nom affiché sur l'application. Vous pourrez
            l'éditer après la création du fichier.
          </p>
        </TextContainer>
      </Form>
    </>
  );
};

export default Create;
